import { useContext, useEffect } from 'react'
import { Outlet } from 'react-router'
import ReactPixel from 'react-facebook-pixel'
import { observer } from 'mobx-react-lite'
import styled from 'styled-components'

import type { CustomWindow } from '../utils'
import { chBoot, chLoadScript } from '../utils/ch'
import { toggleTab } from '../utils/toggleTab'

import { StoresContext } from '../provider/StoreProvider'
import { BottomSheetProvider } from '../provider/BottomSheetProvider'
import { ModalProvider } from '../provider/ModalProvider'

import { useIsMobile } from '../hooks/useIsMobile'
import { useWindowResize } from '../hooks/useWindowResize'
import { useUserInfo } from '../hooks/useUserInfo'

import { GlobalComponents } from '../components/global/GlobalComponents'
import { NavigateTab } from '../components/global/NavigateTab'
import { NeedUpdate } from './NeedUpdate'
import { WebAside } from './WebAside'
import { InvalidAccess } from './InvalidAccess'

import { Color } from '../theme/color'

import 'pretendard/dist/web/static/pretendard.css'
import '../theme/action-sheet.css'
import '../theme/custom.css'

const PIXEL_ID = process.env.REACT_APP_PIXEL_ID ?? '1566616250915803'

const matchUserAgent = (matcher: RegExp) => {
  return window.navigator.userAgent.match(matcher)
}

export const Layout = observer(({ isError }: { isError?: boolean }) => {
  const {
    commonStore: { invalidAccess },
  } = useContext(StoresContext)

  const { isLoading } = useUserInfo()
  const isMobile = useIsMobile()
  const isBreak = useWindowResize(1024)

  const needUpdate =
    process.env.NODE_ENV === 'production' &&
    window.location.pathname.includes('app.artipio.com') &&
    !(window as CustomWindow).CheckAppVersion &&
    (matchUserAgent(/iPhone/gi) || matchUserAgent(/Android/gi))

  useEffect(() => {
    console.log('App mount')

    // 채널톡(ChannelIO) 초기화 코드
    chLoadScript()
    chBoot({})

    // 수동 연동 Meta Pixel 초기화 코드
    ReactPixel.init(PIXEL_ID, undefined, { autoConfig: true, debug: true })
    ReactPixel.pageView()
  }, [])

  // location이 바뀔 때 마다 하단 탭 active 체크
  useEffect(() => {
    const pathname = location.pathname
    toggleTab(pathname)
    ReactPixel.pageView()
  }, [location])

  if (needUpdate) return <NeedUpdate />

  if (isLoading) return null

  return (
    <Wrap isMobile={isMobile}>
      {!isMobile && !isBreak && (
        <WebSideView>
          <WebAside />
        </WebSideView>
      )}
      <AppView $isIPhone={!!matchUserAgent(/iPhone/gi)}>
        <BottomSheetProvider>
          <ModalProvider>
            {isError || invalidAccess ? <InvalidAccess /> : <Outlet />}
            <NavigateTab />
            <GlobalComponents />
          </ModalProvider>
        </BottomSheetProvider>
      </AppView>
    </Wrap>
  )
})
const Wrap = styled.div<{ isMobile: boolean }>`
  display: flex;
  justify-content: center;
  gap: 40px;
  ${({ isMobile }) =>
    isMobile
      ? ''
      : `
        background-color: ${Color.gray_1};
        > ${AppView} {
          border: 1px solid ${Color.gray_3};
        }
        *::-webkit-scrollbar {
          display: none;
        }
        -ms-overflow-style: none;
        scrollbar-width: none;
    `}
`

const WebSideView = styled.div`
  display: flex;
  align-items: center;
  width: 450px;
  height: 100vh;
`

const AppView = styled.div<{ $isIPhone: boolean }>`
  display: flex;
  flex-direction: column;
  position: relative;
  width: 100%;
  height: 100svh;
  max-width: 500px;
  ${({ $isIPhone }) => ($isIPhone ? 'padding-bottom: env(safe-area-inset-bottom);' : '')}
`
